import {makeAutoObservable, toJS} from "mobx";
import yaml from "yaml";
import _ from "lodash";

// Types
import type {File} from "../shared/Types.d.ts";
import {CsProjectT} from "../@types/TypeboxTypes";
import {Project as CafebabeProject} from "../util/cafebabe/src/project";
import CsInstance from "./CsInstance";
import CodeMirrorStore from "./codeMirrorStore";

// Utilities
import {runFetch} from "../util/runFetch";
import {validateClassName} from "../util/validation";
import {removeSnippetComments} from "../util/snippets";
import JAVA_TEMPLATE from "../util/NewActorTemplate.json";

// Cafebabe
import {loadJdk, DEFAULT_JDK_PATH} from "../util/cafebabe/src/fetch_jdk";

/**
 * A map of file names to file data.
 */
type FileMap = {[filename: string]: File};

/**
 * State of a saved project that can be saved and reloaded. Stored in localStorage on a per-project basis.
 */
export type ProjectSaveState = {
	fileMap: FileMap;
	/** @deprecated use fileMap instead */
	fileData?: FileMap;
	filesOpen: string[];
	focusedFile: string | null;
};

/**
 * The type of save operation that occurred.
 */
export enum SaveType {
	Compile = "Compile",
	SwitchProject = "SwitchProject",
	Manual = "Manual",
	NewFile = "NewFile",
	RenameFile = "RenameFile",
	DeleteFile = "DeleteFile",
	SwitchTab = "ChangeTab",
	EditTab = "EditTab",
	Reset = "Reset",
}

/**
 * A function provided by the consumer that runs when a project should be saved.
 * @param projectName The name of the project to save.
 * @param codeState The code state to save.
 * @param saveType The type of save to perform.  Ideally in the future we will
 * 		not need to expose this to the consumer, but we are trying it for now.
 *		See https://aops.slack.com/archives/C06ETAQ2NLX/p1745949109753329
 */
export type SaveFunction = (
	projectName: string,
	codeState: string,
	saveType?: SaveType
) => void;

/**
 * Saves a major version of a project to the DB.
 * A major version will never be overwritten, while a minor version is a temporary save that can be overwritten
 * by another minor or major save.
 */
const MAJOR_SAVE_TYPES = [SaveType.Compile, SaveType.SwitchProject];

/**
 * The default text displayed when no file is open.
 */
export const EMPTY_EDITOR_PROMPT = "\nPlease open a file to start editing\n";

/**
 * MobX store for local file and project handling.
 *
 * It handles:
 * - file creation, deletion, and switching
 * - local storage and saving of files
 * - syncing to the CS (Monaco or CodeMirror)
 */
class CodeStore {
	private _projectName: string | null = null;
	private _serverUrl: string;
	private _projectTemplates: CsProjectT[] = [];
	private _projectNameToIndex: Record<string, number> = {};

	private _hasUncompiledChanges = false; // Tracks if the editor has been changed so we can enable/disable the compile button
	private _isMarkdownEditMode = false;

	/**
	 * Fields that handle the current project state for project loading and saving.
	 * These fields correspond to the ProjectSaveState type.
	 */
	private _fileMap: FileMap = {};
	private _filesOpen: string[] = [];
	private _focusedFile: string | null = null;

	/** Stored cafebabe Project object */
	private _cafebabeProject: CafebabeProject | null = null;

	private _csInstance: CsInstance;
	private _codeMirrorStore: CodeMirrorStore;

	private _minorSaveFunction: SaveFunction = () => {};
	private _majorSaveFunction: SaveFunction = () => {};

	/**
	 * @param props.csInstance The CsInstance to use for compilation and other server-side operations.
	 * @param props.serverUrl The URL of the server to use for server-side operations.
	 * @param props.minorSaveFunction Function to call when a minor save is needed.
	 * @param props.majorSaveFunction Function to call when a major save is needed.
	 */
	constructor(props: {
		csInstance: CsInstance;
		serverUrl: string;
		minorSaveFunction?: (projectName: string, codeState: string) => void;
		majorSaveFunction?: (projectName: string, codeState: string) => void;
	}) {
		this._csInstance = props.csInstance;
		this._codeMirrorStore = new CodeMirrorStore({csInstance: this._csInstance});

		this._serverUrl = props.serverUrl;
		if (this._csInstance.featureFlagStore.loadPrevProject) {
			this.loadProject();
		}
		this._minorSaveFunction =
			props.minorSaveFunction || this._minorSaveFunction;
		this._majorSaveFunction =
			props.majorSaveFunction || this._majorSaveFunction;

		makeAutoObservable(this);
	}

	/**
	 * Given a project name, load it from local storage if it exists, and otherwise use the template.
	 * @param projectName The name of the project; if not provided, then the last-opened project is used.
	 */
	private loadProject(projectName?: string) {
		// If local storage data exists, load it
		const {saveState, localProjectName} =
			this.loadProjectFromLocalStorage(projectName);

		// Set this.projectName (use setter for vanilla codemirror) before getFileData() uses it
		// `localProjectName` *should* be correct over `projectName`
		this.projectName = localProjectName || projectName || this.projectName;

		// If there is no save state, or the file map is empty, load the project files from the template
		if (saveState === null || _.isEmpty(saveState.fileMap)) {
			this.initializeProjectFromTemplate();
			return;
		}

		this.fileMap = saveState.fileMap;
		this.filesOpen = saveState.filesOpen;
		this.focusedFile = saveState.focusedFile;
	}

	/**
	 * Load the given project, returning null for missing entries.
	 *
	 * @param projectName The project to load; if not provided, defaults to the last open project.
	 * @returns The project name and save state if it exists.
	 */
	private loadProjectFromLocalStorage(projectName?: string) {
		const projectNameToLoad =
			projectName ?? localStorage.getItem("mostRecentProjectName");
		const storageLocation = "csProjectData-" + projectNameToLoad;

		const stored = localStorage.getItem(storageLocation);
		return {
			localProjectName: projectNameToLoad,
			saveState: stored ? (JSON.parse(stored) as ProjectSaveState) : null,
		};
	}

	/**
	 * Save the current project to local storage. The project data is stored in the local storage entry
	 * csProjectData-<name>. There are no restrictions on the name.
	 *
	 * This function is called when a minor save action is performed.
	 */
	saveProjectToLocalStorage() {
		if (!this._projectName) {
			console.error("Attempted to save file data with no project");
			return;
		}
		const storageLocation = "csProjectData-" + this._projectName;

		// Save which project we're on
		localStorage.setItem("mostRecentProjectName", this._projectName);

		// Save per-project data
		const saveState: ProjectSaveState = {
			fileMap: this.fileMap,
			filesOpen: this.filesOpen,
			focusedFile: this.focusedFile,
		};

		localStorage.setItem(storageLocation, JSON.stringify(saveState));
	}

	/**
	 * Saves the project using the provided save function.
	 * Save to local storage first, just in case.
	 * @param saveType The type of save to perform.
	 */
	saveProject(saveType: SaveType) {
		this.saveProjectToLocalStorage();
		if (MAJOR_SAVE_TYPES.indexOf(saveType) > -1) {
			this._majorSaveFunction(
				this._projectName || "",
				JSON.stringify({
					fileMap: this.fileMap,
					filesOpen: this.filesOpen,
					focusedFile: this.focusedFile,
				}),
				saveType
			);
		} else {
			this._minorSaveFunction(
				this._projectName || "",
				JSON.stringify({
					fileMap: this.fileMap,
					filesOpen: this.filesOpen,
					focusedFile: this.focusedFile,
				}),
				saveType
			);
		}
	}

	/**
	 * Saves a project template to memory, so it can be switched to later.
	 *
	 * @param projectTemplate The project template to save.
	 */
	cacheProjectTemplate(projectTemplate: CsProjectT) {
		if (!projectTemplate.files?.length) {
			console.error(
				"Attempted to load activity with no files:",
				JSON.stringify(projectTemplate, null, 2)
			);
			return;
		}

		this._projectNameToIndex[projectTemplate.name] =
			this._projectTemplates.length;
		this._projectTemplates.push(projectTemplate);
	}

	/**
	 * Get the list of cached project templates.
	 * Includes any default projects native to the CS app.
	 */
	get validProjectNames(): string[] {
		return Object.keys(this._projectNameToIndex);
	}

	/**
	 * Save the current activity to a YAML.
	 * @param projectName The name of the project to save. If not provided, the
	 * current project name is used.
	 */
	outputActivityYaml(projectName?: string): string {
		const yamlObj: CsProjectT = {
			name: projectName || this._projectName || "",
			"ide-mode": "full",
			files: Object.keys(this._fileMap).map((name) => ({
				name,
				attributes: {
					open: this._filesOpen.includes(name),
					hidden: this._fileMap[name]?.attributes?.isHidden,
					isMarkdown: this._fileMap[name]?.attributes?.isMarkdown,
					isReadOnly: this._fileMap[name]?.attributes?.isReadOnly,
				},
				content: this._fileMap[name]?.value || "",
			})),
		};

		return yaml.stringify(yamlObj);
	}

	/**
	 * Get the files of the current project from a local template (if it
	 * exists). Then, overwrite all local file data, and open the
	 * default hidden file.
	 *
	 * Note: dependent on this._project, this._projectNameToIndex, and
	 * this._projectTemplates.
	 */
	initializeProjectFromTemplate() {
		if (!this._projectName) {
			console.error("Attempted to load files with no project");
			return;
		}
		const templateIndex = this._projectNameToIndex[this._projectName];

		// if template exists, load it from the template (has an index aka index is a number, 0 is ok)
		if (typeof templateIndex === "number") {
			const template = this._projectTemplates[templateIndex];

			if (!template) {
				console.error(
					"internal CS error: template index stored but template not found",
					toJS(this._projectNameToIndex),
					toJS(this._projectTemplates)
				);
				return;
			}

			// clear out old data by creating new objects, for mobx
			const newHiddenFileNames: string[] = [];
			const newFileNames: string[] = [];
			const newFilesOpen: string[] = [];
			const newFileMap: FileMap = {};

			// fill in new data
			this.projectName = template.name; // Use setter for vanilla codemirror
			template.files.forEach((file) => {
				// TODO if the student has preexisting data, load it in properly

				// Hidden files should not be shown to users
				if (file.attributes?.hidden) {
					newHiddenFileNames.push(file.name);
				} else {
					newFileNames.push(file.name);
				}

				// Strip out snippet markers
				const content = removeSnippetComments(file.content);
				newFileMap[file.name] = {
					name: file.name,
					value: content,
					attributes: {
						isOriginal: true,
						isHidden: file.attributes?.hidden,
						isMarkdown: file.attributes?.isMarkdown,
						isReadOnly: file.attributes?.isReadOnly,
					},
				};

				if (!file.attributes?.hidden && file.attributes?.open) {
					newFilesOpen.push(file.name);
				}
			});

			this._filesOpen = newFilesOpen;
			this._fileMap = newFileMap;

			// Use the setter to update codemirror's editor content
			this.focusedFile = this._filesOpen[0] || "";

			return;
		}
	}

	/**
	 * Sets the value of one File in fileMap
	 * @param key The key of the File to set
	 * @param value The File to set
	 */
	setFileData(key: string, value: File) {
		if (this._fileMap[key]) {
			this._fileMap[key] = value;
		}
	}

	/**
	 * Resets a file to its original template value. If the file was created by the student,
	 * it will be deleted.
	 * @param fileName The name of the file to reset.
	 */
	resetFile(fileName: string) {
		this.hasUncompiledChanges = true;

		const isOriginal = this._fileMap[fileName]?.attributes?.isOriginal ?? false;

		if (!isOriginal) {
			this.deleteFile(fileName);
			return;
		}

		if (!this._projectName) {
			console.error("Attempted to reset file with no project");
			return;
		}

		const template =
			this._projectTemplates[this._projectNameToIndex[this._projectName]!];

		if (!template) {
			console.error(
				"internal CS error: template index stored but template not found",
				toJS(this._projectNameToIndex),
				toJS(this._projectTemplates)
			);
			return;
		}

		const originalFile = template.files.find((file) => file.name === fileName);
		if (!originalFile) {
			console.error("Attempted to reset file that does not exist");
			return;
		}

		const content = removeSnippetComments(originalFile.content);
		this.setFileData(originalFile.name, {
			name: originalFile.name,
			value: content,
			attributes: {
				isOriginal: true,
				isHidden: originalFile.attributes?.hidden,
				isMarkdown: originalFile.attributes?.isMarkdown,
				isReadOnly: originalFile.attributes?.isReadOnly,
			},
		});
		this._codeMirrorStore.updateEditorContentFromFocusedFile();
	}

	/**
	 * Resets the whole project to its original template.
	 */
	resetProject() {
		this.initializeProjectFromTemplate();
		this.saveProject(SaveType.Reset);
	}

	/**
	 * Clears the local storage and reloads the project. The server is notified of the reset for analytics.
	 */
	async resetLocalStorageAndReload() {
		// Log that the reset was initiated, don't need the fetch response since this is just logging
		try {
			await runFetch({
				url: `${this._csInstance.serverUrl}/api/v2/log`,
				method: "POST",
				body: {
					logGroup: "resetLocalStorage",
					value: {project: this._projectName, data: this._fileMap},
				},
				securityToken: this._csInstance.securityToken,
			});
		} catch (e) {}

		localStorage.clear();
		if (this._projectName) {
			this.loadProject(this._projectName);
		}
	}

	/**
	 * Switches to the next project in the project list.
	 */
	nextProject() {
		if (!this._projectName) {
			console.error("Attempted to switch project with no project");
			return;
		}
		const currIdx = this._projectNameToIndex[this._projectName];
		const nextIdx = (currIdx! + 1) % this._projectTemplates.length;
		this.switchProject(this._projectTemplates[nextIdx]!.name);
	}

	/**
	 * Switches project to a specific project if it exists.
	 * @param projectName The project to switch to
	 * @param shouldCompile Whether to compile the code after switching
	 * @param codeState Optional code state to switch to
	 */
	switchProject(
		projectName: string,
		shouldCompile = true,
		codeState?: ProjectSaveState
	) {
		if (!this.validProjectNames.includes(projectName)) {
			console.error(
				"invalid project name",
				projectName,
				", need",
				this.validProjectNames
			);
			return;
		}
		this.saveProject(SaveType.SwitchProject);
		const template = JSON.parse(
			JSON.stringify(
				this._projectTemplates[this._projectNameToIndex[projectName]!]!
			)
		);
		if (template && template.multiplayerClass) {
			this._csInstance.multiplayerStore.isMultiplayer = true;
			this._csInstance.multiplayerStore.trackedClass =
				template.multiplayerClass;
			this._csInstance.multiplayerStore.startMultiplayerInterval();
		} else {
			this._csInstance.multiplayerStore.isMultiplayer = false;
			this._csInstance.multiplayerStore.trackedClass = "";
			this._csInstance.multiplayerStore.stopMultiplayerInterval();
		}
		if (codeState) {
			// Use setter for vanilla codemirror
			this.projectName = projectName;
			// Set these directly to bypass extra saveProject calls
			this._fileMap = codeState.fileMap || codeState.fileData;
			this._filesOpen = codeState.filesOpen;
			// Use the setter to update codemirror's editor content
			this.focusedFile = codeState.focusedFile;
		} else {
			this.loadProject(projectName);
		}
		if (shouldCompile) {
			this.compileCode();
		}
	}

	/**
	 * Add a student class subclassing Actor.
	 * @param derived Name of the student class to add.
	 * @returns The Java code for the new class.
	 * @throws {Error} Error if the class name is invalid, or there already exists a file that defines the class.
	 */
	addActorClass(derived: string): string {
		const validate = validateClassName(derived, this.fileNames);
		if (validate?.type === "error") throw new Error(validate.message);

		const javaCode = JAVA_TEMPLATE.replace(/\$\{derived}/g, derived);
		const fileName = derived + ".java";
		this._fileMap[fileName] = {name: fileName, value: javaCode};

		if (!this.filesOpen.includes(fileName)) {
			this.filesOpen.push(fileName);
			this.focusedFile = fileName;
		}

		this.saveProject(SaveType.NewFile);
		return javaCode;
	}

	/**
	 * Rename a file in the editor.
	 * @param oldFileName currently existing file name
	 * @param newClass file name to change to, minus .java extension
	 */
	renameActorClass(oldFileName: string, newClass: string) {
		const validate = validateClassName(newClass, this.fileNames);
		if (validate?.type === "error") throw new Error(validate.message);

		const newFileName = newClass + ".java";

		const file = this._fileMap[oldFileName];
		if (!file) throw new Error(`File ${oldFileName} does not exist.`);

		const newFileData = {...file, name: newFileName};
		this._fileMap[newFileName] = newFileData;
		delete this._fileMap[oldFileName];

		const j = this._filesOpen.indexOf(oldFileName);
		if (j !== -1) {
			this._filesOpen[j] = newFileName;
		}

		if (this._focusedFile === oldFileName) {
			this._focusedFile = newFileName;
		}

		this._codeMirrorStore.updateEditorContent(newFileData.value);
		this.saveProject(SaveType.RenameFile);
	}

	/**
	 * Focuses a file in the editor. This happens when a file is clicked in the
	 * file tree, file tab, CodeEditorMenu, or when a new file is created.
	 * CodeStore's `focusedFile` is set to the file name, and the file's contents
	 * are set in the editor.
	 * @param fileData The file to focus. Contains the filename and value.
	 */
	focusFile(fileData: File) {
		const filename = fileData.name;
		// If the file is already focused, do nothing
		if (this._focusedFile === filename) {
			return;
		}

		// Use the setter to update codemirror's editor content
		this.focusedFile = filename;
	}

	/**
	 * @param fileName File to delete.
	 */
	deleteFile(fileName: string) {
		if (this.focusedFile === fileName) {
			// need to move our focus
			const i = this.filesOpen.indexOf(fileName);
			const l = this.filesOpen.length;
			if (i !== -1 && l > 1) {
				// Prefer the file to the right of the original
				this.focusedFile = this.filesOpen[i === l - 1 ? l - 2 : i + 1]!;
				this._codeMirrorStore.updateEditorContent(
					this.fileMap[this.focusedFile]?.value ||
						"Error: Please notify your teacher of this error."
				);
			} else {
				this.focusedFile = null;
				this._codeMirrorStore.showEmptyEditorPrompt();
			}
		}

		// Delete everywhere
		this.filesOpen = this.filesOpen.filter((name) => name !== fileName);
		delete this.fileMap[fileName];

		this.saveProject(SaveType.DeleteFile);
	}

	/**
	 * Get the list of YAMLs from the server side.
	 */
	async getServerSideYamls() {
		const {yamls: currentYamls} = (await runFetch({
			url: `${this._csInstance.serverUrl}/api/v2/getCurrentYamls`,
			method: "GET",
			securityToken: this._csInstance.securityToken,
		})) as {yamls: string[]};
		return currentYamls;
	}

	/**
	 * Set the server side YAMLs.
	 *
	 * @param yamls The YAMLs to set.
	 * @param shouldOverwrite Whether to overwrite the existing YAMLs. Defaults to
	 * 		false.
	 */
	setServerSideYamls(yamls: string[], shouldOverwrite = false) {
		runFetch({
			url: `${this._csInstance.serverUrl}/api/v2/setYaml`,
			method: "POST",
			body: {
				yamls,
				shouldOverwrite,
			},
			securityToken: this._csInstance.securityToken,
		});
	}

	/**
	 * Performs some sanity checks and then calls compileStore's compileCode function.
	 */
	async compileCode() {
		if (!this._projectName) {
			console.error("Attempted to compile code with no project chosen");
			return;
		}
		const fileDataToCompile = Object.fromEntries(
			Object.entries(this._fileMap).filter(([fileName, file]) => {
				return !file.attributes?.isMarkdown;
			})
		);

		this.saveProject(SaveType.Compile);
		await this._csInstance.compileStore.compileCode(
			this._projectName,
			fileDataToCompile,
			this.hiddenFileNames
		);
	}

	get projectTemplates() {
		return [...this._projectTemplates];
	}

	/**
	 * Filters codeStore.fileMap to only return files with the `isHidden` attribute set to false.
	 *
	 * @returns Array of filenames visible to the user.
	 */
	get visibleFileNames(): string[] {
		return Object.keys(this._fileMap).filter(
			(fileName) =>
				this._csInstance.featureFlagStore.showHiddenFiles ||
				!(this._fileMap[fileName] || {}).attributes?.isHidden
		);
	}

	/**
	 * Filters codeStore.fileMap to only return files with the `isHidden` attribute set to true.
	 *
	 * @returns Array of filenames hidden from the user.
	 */
	get hiddenFileNames(): string[] {
		return Object.keys(this._fileMap).filter(
			(fileName) =>
				!this._csInstance.featureFlagStore.showHiddenFiles &&
				(this._fileMap[fileName] || {}).attributes?.isHidden
		);
	}

	/**
	 * Creates a FileMap whose keys are only the filenames visible to the user, and maps them to their respective file data.
	 *
	 * @returns Map of filenames to file data ONLY for files visible to the user.
	 */
	get visibleFileMap(): FileMap {
		return this.visibleFileNames.reduce((accum, fileName) => {
			accum[fileName] = this._fileMap[fileName]!;
			return accum;
		}, {} as FileMap);
	}

	// getters and setters for MobX actions

	get codeMirrorStore() {
		return this._codeMirrorStore;
	}

	/**
	 * Gets ALL filenames, including hidden files.
	 * If you want only visible files, use `visibleFileNames`.
	 */
	get fileNames() {
		return Object.keys(this._fileMap);
	}

	/**
	 * Gets ALL file data, including hidden files.
	 * If you want only visible files, use `visibleFileMap`.
	 */
	get fileMap() {
		return this._fileMap;
	}

	set fileMap(value) {
		this._fileMap = value;
	}

	get focusedFile() {
		return this._focusedFile;
	}

	set focusedFile(value) {
		this._focusedFile = value;
		if (value && !this._filesOpen.includes(value)) {
			this._filesOpen = this.filesOpen.concat(value);
		}
		this.saveProject(SaveType.SwitchTab);

		this._codeMirrorStore.updateEditorContentFromFocusedFile();
	}

	get filesOpen() {
		return this._filesOpen;
	}

	set filesOpen(value) {
		this._filesOpen = value;
		this.saveProject(SaveType.EditTab);
	}

	get projectName() {
		return this._projectName;
	}

	set projectName(value) {
		this._projectName = value;

		// Load the JDK and create a project
		(async () => {
			const jdk = await loadJdk(async () => {
				return runFetch({
					url: this.serverUrl + DEFAULT_JDK_PATH,
					method: "GET",
				});
			});

			const fileMap = this.fileMap;
			const payload = Object.fromEntries(
				[...Object.entries(fileMap)].map(([filename, file]) => [
					filename,
					file.value,
				])
			);

			this.cafebabeProject = new CafebabeProject({jdk, payload});
		})();
	}

	get hasUncompiledChanges() {
		return this._hasUncompiledChanges;
	}

	set hasUncompiledChanges(value) {
		this._hasUncompiledChanges = value;
	}

	get serverUrl() {
		return this._serverUrl;
	}

	set serverUrl(value) {
		this._serverUrl = value;
	}

	get isMarkdownEditMode() {
		return this._isMarkdownEditMode;
	}
	set isMarkdownEditMode(value) {
		this._isMarkdownEditMode = value;
	}

	get cafebabeProject() {
		return this._cafebabeProject;
	}
	set cafebabeProject(value) {
		this._cafebabeProject = value;
	}

	set minorSaveFunction(
		func: (projectName: string, codeState: string) => void
	) {
		this._minorSaveFunction = func;
	}

	set majorSaveFunction(
		func: (projectName: string, codeState: string) => void
	) {
		this._majorSaveFunction = func;
	}
}

export default CodeStore;
